import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  CircleDot,
  Clock3,
} from 'lucide-react';
import {
  type CatchballLoop,
  type CatchballLoopState,
  type BallPossession,
  type BallStance,
  getBallPossession,
  getBallStance,
  LOOP_STATE_LABELS,
} from '../../../types/prioritization/catchballLoop';
import { useOrgAwareCatchball } from '../../../hooks/prioritization/useOrgAwareCatchball';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useCatchballTierTurnStore } from '../../../stores/prioritization/catchballTierTurnStore';
import { useCatchballBallStore } from '../../../stores/prioritization/catchballBallStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { useLoopDisplayLabel } from './loopDisplayLabel';
import { BallStrip } from './BallStrip';
import { isAnchorBall } from '../../../types/prioritization/catchballBall';

type StationId = BallPossession;

type StationDef = { id: StationId; label: string; tier: BallPossession; depth?: number };

type TrackerAction = {
  href: string;
  label: string;
};

type TransitSegment = {
  connector: 'upper' | 'lower';
  direction: 'forward' | 'reverse';
};

const STATIONS: Array<{ id: StationId; label: string }> = [
  { id: 'executive', label: 'Executive' },
  { id: 'middle', label: 'Next level' },
  { id: 'bottom', label: 'Lower level' },
];

function cx(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

function getTransitSegment(state: CatchballLoopState): TransitSegment | null {
  switch (state) {
    case 'tossed_to_middle':
      return { connector: 'upper', direction: 'forward' };
    case 'tossed_to_bottom':
      return { connector: 'lower', direction: 'forward' };
    case 'returned_to_middle':
      return { connector: 'lower', direction: 'reverse' };
    case 'returned_to_top':
      return { connector: 'upper', direction: 'reverse' };
    default:
      return null;
  }
}

function getStanceBadge(stance: BallStance): {
  label: string;
  lightClass: string;
  darkStyle: string;
  icon: typeof ArrowDownRight;
} {
  switch (stance) {
    case 'proposal':
      return {
        label: '↑ Proposal',
        lightClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
        darkStyle: 'rgba(61,75,111,0.18)',
        icon: ArrowUpRight,
      };
    case 'transit-up':
      return {
        label: '↑ Proposal',
        lightClass: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
        darkStyle: 'rgba(180,130,30,0.15)',
        icon: ArrowUpRight,
      };
    case 'complete':
      return {
        label: 'Complete',
        lightClass: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
        darkStyle: 'rgba(58,125,86,0.15)',
        icon: CheckCircle2,
      };
    case 'transit-down':
      return {
        label: '↓ Request',
        lightClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
        darkStyle: 'rgba(20,100,160,0.15)',
        icon: ArrowDownRight,
      };
    case 'request':
    default:
      return {
        label: '↓ Request',
        lightClass: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
        darkStyle: 'rgba(255,255,255,0.06)',
        icon: ArrowDownRight,
      };
  }
}

function getContextualAction(loop: CatchballLoop, cycleId: string): TrackerAction {
  switch (loop.state) {
    case 'drafting':
      return {
        href: `/prioritization/cascade/compose/${cycleId}`,
        label: 'Compose request →',
      };
    case 'middle_working':
    case 'tossed_to_bottom':
    case 'bottom_working':
    case 'returned_to_middle':
    case 'accepted_provisional':
    case 'returned':
    case 'rejected':
    case 'OPEN':
    case 'REVISION_REQUESTED':
      return {
        href: `/prioritization/cascade/workbench/${cycleId}/${loop.id}`,
        label: 'Open loop workspace →',
      };
    case 'executive_review':
    case 'proposed':
    case 'PROPOSED':
      return {
        href: `/prioritization/cascade/inbox/${cycleId}`,
        label: 'Review proposal →',
      };
    case 'output_ready':
    case 'ACCEPTED':
    case 'FINAL_REJECTED':
    case 'closed':
    case 'DECLINED':
      return {
        href: `/prioritization/cascade/dashboard/${cycleId}`,
        label: 'View dashboard →',
      };
    case 'tossed_to_middle':
      return {
        href: `/prioritization/cascade/dashboard/${cycleId}`,
        label: 'Track handoff →',
      };
    case 'returned_to_top':
      return {
        href: `/prioritization/cascade/inbox/${cycleId}`,
        label: 'Open executive review →',
      };
    default:
      return {
        href: `/prioritization/cascade/dashboard/${cycleId}`,
        label: 'Open dashboard →',
      };
  }
}

function formatRelativeTime(value: Date, now: number): string {
  const diffMs = value.getTime() - now;
  const absSeconds = Math.abs(Math.round(diffMs / 1000));
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  if (absSeconds < 60) {
    return formatter.format(Math.round(diffMs / 1000), 'second');
  }

  const absMinutes = Math.abs(Math.round(diffMs / 60000));
  if (absMinutes < 60) {
    return formatter.format(Math.round(diffMs / 60000), 'minute');
  }

  const absHours = Math.abs(Math.round(diffMs / 3600000));
  if (absHours < 24) {
    return formatter.format(Math.round(diffMs / 3600000), 'hour');
  }

  return formatter.format(Math.round(diffMs / 86400000), 'day');
}

export function BallTrackerStrip({
  loop,
  cycleId,
  currentUserId,
}: {
  loop: CatchballLoop | null;
  cycleId: string;
  currentUserId?: string;
}) {
  const [now, setNow] = useState(() => Date.now());
  const orgAware = useOrgAwareCatchball(loop, currentUserId ?? null);
  const orgUnitsMap = useEnterpriseIdentityStore((state) => state.orgUnits);
  const fetchBallsForLoop = useCatchballBallStore((s) => s.fetchBallsForLoop);
  const getBallsForLoop   = useCatchballBallStore((s) => s.getBallsForLoop);
  const loadLoopTurns     = useCatchballTierTurnStore((s) => s.loadLoop);
  const fetchTracksForCycle = useTrackStore((s) => s.fetchTracksForCycle);
  const loopLabel = useLoopDisplayLabel(loop);

  useEffect(() => {
    if (loop?.id) fetchBallsForLoop(loop.id);
  }, [loop?.id, fetchBallsForLoop]);

  // Track names are needed to disambiguate per-track loop numbering in the
  // eyebrow label; some hosts (chrome, proposal review) don't fetch tracks.
  useEffect(() => {
    if (cycleId) void fetchTracksForCycle(cycleId);
  }, [cycleId, fetchTracksForCycle]);

  // Arm the level-turns read so the tracker is turns-first even when shown outside
  // the workbench (the chrome renders it on other sub-views). Idempotent; the
  // workbench owns the realtime subscription for live updates.
  useEffect(() => {
    if (loop?.id) void loadLoopTurns(loop.id);
  }, [loop?.id, loadLoopTurns]);

  const balls = loop ? getBallsForLoop(loop.id).filter((ball) => !isAnchorBall(ball)) : [];

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const possession = loop ? getBallPossession(loop.state) : null;
  const stance = loop ? getBallStance(loop.state) : null;
  const transit = loop ? getTransitSegment(loop.state) : null;

  const getUserOrgNode = useEnterpriseIdentityStore((s) => s.getUserOrgNode);

  const allUnits = useMemo(() => Array.from(orgUnitsMap.values()), [orgUnitsMap]);

  // The originator is whoever issued the request (or created the loop).
  // Their org-unit depth defines the "executive" station for this tracker —
  // any nodes above them in the tree are outside the loop's scope.
  const originatorDepth = useMemo(() => {
    if (!loop) return null;
    const originatorId = loop.requestIssuedBy ?? loop.createdBy;
    if (!originatorId) return null;
    return getUserOrgNode(originatorId)?.depth ?? null;
  }, [loop, getUserOrgNode]);

  const action = useMemo(
    () => (loop ? getContextualAction(loop, cycleId) : null),
    [loop, cycleId]
  );
  const activeOrgDepth = useMemo(() => {
    // Turns-first (ADR-catchball-consolidation): when a level turn exists, the ball
    // holder's org-unit depth is authoritative — the turn record wins outright over
    // the legacy loop.state tier mapping. holderDepth comes from useOrgAwareCatchball,
    // which reads catchball_active_branch_positions. The loop.state switch below is the
    // fallback only for legacy loops that have no turns yet.
    if (orgAware.holderDepth != null) return orgAware.holderDepth;

    if (!loop || !orgAware.rootNode || orgAware.userTier == null) return null;

    const levelOneIds =
      (loop.intent?.recipientOrgUnitIds?.length
        ? loop.intent.recipientOrgUnitIds
        : allUnits
            .filter((unit) => unit.parentId === orgAware.rootNode!.id)
            .map((unit) => unit.id)) ?? [];
    const levelOneDepths = (
      levelOneIds.length > 0
        ? levelOneIds.map((id) => allUnits.find((unit) => unit.id === id)?.depth)
        : allUnits
            .filter((unit) => unit.parentId === orgAware.rootNode!.id)
            .map((unit) => unit.depth)
    ).filter((depth): depth is number => typeof depth === 'number');
    const nextDepth =
      levelOneDepths.length > 0 ? Math.min(...levelOneDepths) : orgAware.rootNode.depth + 1;
    const lowerLevelDepths = allUnits
      .filter((unit) => levelOneIds.includes(unit.parentId ?? ''))
      .map((unit) => unit.depth);
    const lowerDepth =
      lowerLevelDepths.length > 0 ? Math.min(...lowerLevelDepths) : nextDepth;

    // The "executive" depth for this loop is the originator's depth when known,
    // otherwise the root node's depth. This prevents the tracker from showing
    // nodes above the person who actually issued the loop.
    const executiveDepth = originatorDepth ?? orgAware.rootNode.depth;

    switch (loop.state) {
      case 'drafting':
      case 'executive_review':
      case 'output_ready':
      case 'closed':
      case 'PROPOSED':
      case 'FINAL_REJECTED':
      case 'DECLINED':
        return executiveDepth;
      case 'tossed_to_middle':
      case 'middle_working':
      case 'OPEN':
      case 'REVISION_REQUESTED':
      case 'ACCEPTED':
      case 'returned_to_middle':
        return nextDepth;
      case 'tossed_to_bottom':
      case 'bottom_working':
      case 'accepted_provisional':
      case 'returned':
      case 'rejected':
        return lowerDepth;
      case 'returned_to_top':
      case 'proposed':
      default:
        return executiveDepth;
    }
  }, [loop, orgAware.rootNode, orgAware.userTier, orgAware.holderDepth, allUnits, originatorDepth]);

  if (!loop) {
    return (
      <div className="border border-dashed border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] px-4 py-3 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] font-light">
        No active loop — start one to begin
      </div>
    );
  }

  if (!orgAware.canSeeLoop) {
    return (
      <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3 text-sm text-[var(--mustard)]">
        This loop is outside your org scope.
      </div>
    );
  }

  const stations: StationDef[] =
    orgAware.userTier != null && orgAware.rootNode
      ? (() => {
          const fullSubtree = allUnits.filter(
            (u) => u.path?.includes(orgAware.rootNode!.id) || u.id === orgAware.rootNode!.id
          );

          // When the proposal was addressed to specific routes, limit the tracker
          // to only those branches so we don't show irrelevant org units.
          const recipientIds = new Set(loop.intent?.recipientOrgUnitIds ?? []);
          const subtree =
            recipientIds.size === 0
              ? fullSubtree
              : fullSubtree.filter((u) => {
                  // Always keep the root (executive level)
                  if (u.id === orgAware.rootNode!.id) return true;
                  // Keep recipients themselves
                  if (recipientIds.has(u.id)) return true;
                  // Keep ancestors of recipients (root→recipient path)
                  const isAncestorOfRecipient = fullSubtree
                    .filter((r) => recipientIds.has(r.id))
                    .some((r) => r.path?.includes(u.id));
                  if (isAncestorOfRecipient) return true;
                  // Keep descendants of recipients (sub-units within the route)
                  const isDescendantOfRecipient = u.path?.some((id) => recipientIds.has(id));
                  if (isDescendantOfRecipient) return true;
                  return false;
                });

          const byDepth = new Map<number, typeof subtree>();
          subtree.forEach((u) => {
            const list = byDepth.get(u.depth) ?? [];
            list.push(u);
            byDepth.set(u.depth, list);
          });
          const allDepths = [...byDepth.keys()].sort((a, b) => a - b);
          // Drop any depth levels above the loop originator — those nodes are
          // outside the loop's scope and should not appear in the tracker.
          const minDepth = originatorDepth ?? orgAware.rootNode.depth;
          const depths = allDepths.filter((d) => d >= minDepth);
          const rootDepth = orgAware.rootNode.depth;
          const maxDepth = Math.max(...depths, rootDepth);
          return depths.map((d) => {
            const unitsAtDepth = byDepth.get(d) ?? [];
            const label =
              unitsAtDepth.length === 0
                ? (d === rootDepth ? orgAware.rootNode!.displayName : `Level ${d}`)
                : unitsAtDepth.length === 1
                  ? unitsAtDepth[0].displayName
                  : unitsAtDepth.length <= 3
                    ? unitsAtDepth.map((u) => u.displayName).join(' · ')
                    : `${unitsAtDepth.slice(0, 2).map((u) => u.displayName).join(' · ')} +${unitsAtDepth.length - 2}`;
            const tier: BallPossession =
              d <= rootDepth ? 'executive' : d >= maxDepth ? 'bottom' : 'middle';
            return {
              id: tier as StationId,
              label,
              tier,
              depth: d,
            };
          });
        })()
      : STATIONS.map((s) => ({ ...s, tier: s.id }));

  const stanceBadge = getStanceBadge(stance ?? 'request');
  const StanceIcon = stanceBadge.icon;

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-4 py-4">
      <style>
        {`
          @keyframes ballTrackerForward {
            0% { transform: translateX(0); opacity: 0.6; }
            50% { opacity: 1; }
            100% { transform: translateX(calc(100% - 0.75rem)); opacity: 0.6; }
          }
          @keyframes ballTrackerReverse {
            0% { transform: translateX(0); opacity: 0.6; }
            50% { opacity: 1; }
            100% { transform: translateX(calc(-100% + 0.75rem)); opacity: 0.6; }
          }
          @keyframes ballGlow {
            0%, 100% { box-shadow: 0 0 0 3px rgba(212,160,23,0.15); }
            50% { box-shadow: 0 0 0 6px rgba(212,160,23,0.25); }
          }
        `}
      </style>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          {/* Eyebrow labels */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="font-mono text-[0.52rem] tracking-[0.18em] uppercase text-[var(--text-ter)] dark:text-[var(--text-dim)]">
              {loopLabel}
            </span>
            <span className="text-[var(--text-ter)] dark:text-[var(--rule-bright)] opacity-60">·</span>
            <span
              className={cx(
                'inline-flex items-center gap-1 font-mono text-[0.52rem] tracking-[0.12em] uppercase px-1.5 py-0.5 rounded',
                stanceBadge.lightClass
              )}
            >
              <StanceIcon className="h-2.5 w-2.5" />
              {stanceBadge.label}
            </span>
            <span className="text-[var(--text-ter)] dark:text-[var(--rule-bright)] opacity-60">·</span>
            <span className="font-mono text-[0.52rem] tracking-[0.1em] uppercase text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {LOOP_STATE_LABELS[loop.state]}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] font-light">
            <span className="inline-flex items-center gap-1">
              <Clock3 className="h-3.5 w-3.5 opacity-60" />
              Updated {formatRelativeTime(loop.updatedAt, now)}
            </span>
            {action && (
              <Link
                to={action.href}
                className="font-medium text-[var(--mustard,#D4A017)] hover:opacity-80 transition-opacity"
              >
                {action.label}
              </Link>
            )}
          </div>
          {(orgAware.currentHolderLabel || orgAware.activityPartnerLabel) && (
            <div className="mt-2 space-y-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] font-light">
              {orgAware.currentHolderLabel && (
                <p>
                  Ball custody:{' '}
                  <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-sec)]">{orgAware.currentHolderLabel}</span>
                </p>
              )}
              {orgAware.activityPartnerLabel && (
                <p>
                  Collaborating:{' '}
                  <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-sec)]">{orgAware.currentHolderLabel || 'Current holder'}</span>
                  {' '}with{' '}
                  <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-sec)]">{orgAware.activityPartnerLabel}</span>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Ball chip strip (Ball model) or single-station ladder (legacy) */}
        <div className="w-full max-w-2xl lg:min-w-[32rem]">
          {balls.length > 0 ? (
            <BallStrip loopId={loop.id} cycleId={cycleId} compact />
          ) : (
          <div
            className="grid items-center gap-2"
            style={{
              gridTemplateColumns: stations
                .flatMap((_, i) => (i < stations.length - 1 ? ['1fr', '2rem'] : ['1fr']))
                .join(' '),
            }}
          >
            {stations.map((station, index) => {
              const isActive =
                activeOrgDepth != null && station.depth != null
                  ? activeOrgDepth === station.depth
                  : possession === station.tier;
              const isUserStation = orgAware.userTier === station.tier;
              const showYoureHere = orgAware.ballIsWithMe && isUserStation;
              const connectorId = index === 0 ? 'upper' : index === 1 ? 'lower' : null;
              const showTransit = connectorId != null && transit?.connector === connectorId;

              return (
                <Fragment key={`${station.id}-${index}`}>
                  <div className="flex flex-col items-center text-center">
                    <div
                      className={cx(
                        'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all',
                        isActive
                          ? 'border-[var(--mustard,#D4A017)] bg-[var(--mustard,#D4A017)] text-white'
                          : 'border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-ter)] dark:text-[var(--text-dim)]'
                      )}
                      style={isActive ? { animation: 'ballGlow 2s ease-in-out infinite' } : undefined}
                    >
                      {isActive ? (
                        <CircleDot className="h-4.5 w-4.5" />
                      ) : (
                        <div className="h-2 w-2 rounded-full bg-current" />
                      )}
                    </div>
                    <span
                      className={cx(
                        'mt-1.5 font-mono text-[0.5rem] tracking-[0.1em] uppercase',
                        isActive
                          ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'
                          : 'text-[var(--text-ter)] dark:text-[var(--text-dim)]'
                      )}
                    >
                      {station.label}
                    </span>
                    {showYoureHere && (
                      <span className="mt-0.5 font-mono text-[0.45rem] tracking-[0.1em] uppercase text-[var(--mustard,#D4A017)]">
                        ● you
                      </span>
                    )}
                  </div>

                  {index < stations.length - 1 && (
                    <div className="relative h-px rounded-full bg-[var(--hover-tint)] dark:bg-[var(--rule-bright)]">
                      <div
                        className={cx(
                          'absolute inset-y-0 left-0 rounded-full',
                          showTransit ? 'right-0 bg-[var(--mustard,#D4A017)]/50' : 'right-auto w-0'
                        )}
                      />
                      {showTransit && (
                        <div
                          className="absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-[var(--mustard,#D4A017)]"
                          style={{
                            left: transit.direction === 'forward' ? 0 : undefined,
                            right: transit.direction === 'reverse' ? 0 : undefined,
                            animation:
                              transit.direction === 'forward'
                                ? 'ballTrackerForward 1.5s ease-in-out infinite alternate'
                                : 'ballTrackerReverse 1.5s ease-in-out infinite alternate',
                          }}
                        />
                      )}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BallTrackerStrip;
